import React, {useState} from "react";
import {
  EllipsisVerticalIcon,
  InformationCircleIcon,
  PlusIcon,
  XMarkIcon,
  ListBulletIcon
} from "@heroicons/react/24/outline";
import {DragDropContext, Droppable, Draggable} from "react-beautiful-dnd";
import _ from "lodash";

// UI
import Input from "../ui-components/Input";
import Textarea from "../ui-components/Textarea";
import Placeholder from "../ui-components/Placeholder";
import {PageSection} from "../ui-components/Container";
import Tooltip from "../ui-components/Tooltip";

const ReferenceJsonEditorSection = ({
    intl,
    push,
    title,
    editable,
    structure,
    value,
    editedValue,
    setEditedValue,
    onEdit=() => {},
    onSave=async (obj) => {},
    onCancel=() => {},
    valueNotAvailableMessage,
    children
}) => {

  const [loading, setLoading] = useState(false);

  const handleContentsItemDrop = (droppedItem) => {
    // ignore drop outside droppable container
    if (!droppedItem.destination || !editedValue)
      return;
    // select contents section
    const targetSection = droppedItem.source.droppableId;
    const updatedList = [...editedValue[targetSection]];
    // remove dragged item
    const [reorderedItem] = updatedList.splice(droppedItem.source.index, 1);
    // add dropped item
    updatedList.splice(droppedItem.destination.index, 0, reorderedItem);
    // update State
    setEditedValue({...editedValue, [targetSection]: updatedList});
  };

  const renderEditableList = (field, required, allowNesting=false, parentIdx=null, rows=2) => {

    const droppableId = field + (parentIdx ? `-${parentIdx}` : '');
    const elements = parentIdx === null
      ? editedValue[field]
      : editedValue[field][parentIdx].items;

    const insertAfter = (idx, parentIdx=null) => {
      const original = [...editedValue[field]];  // clone
      if (parentIdx !== null)
        original[parentIdx].items.splice(idx + 1, 0, '')
      else
        original.splice(idx + 1, 0, '');
      setEditedValue({...editedValue, [field]: original})
    }
    const deleteAt = (idx, parentIdx=null) => {
      const original = [...editedValue[field]];  // clone
      if (parentIdx !== null) {
        if (original[parentIdx].items.length === 1)
          original[parentIdx] = original[parentIdx].title;  // nested item becomes simple item
        else
          original[parentIdx].items.splice(idx, 1);  // delete child item
      }
      else
        original.splice(idx, 1);

      setEditedValue({...editedValue, [field]: original})
    }
    const makeNested = (idx) => {
      const original = [...editedValue[field]];  // clone
      original[idx] = {
        title: original[idx],
        items: ['']
      }
      setEditedValue({...editedValue, [field]: original})
    }
    const updateValue = (value, idx, parentIdx=null) => {
      let updatedValue;
      if (parentIdx === null && typeof editedValue[field][idx] === 'object') {
        // CASE A: target item and has sub-items --> update its title
        updatedValue = editedValue[field].map((orig, j) => idx === j ? {...orig, title: value} : orig);
      }
      else if (parentIdx === null) {
        // CASE B: target item is root-level and doesn't have sub-items (i.e. it's a simple list element) --> update it
        updatedValue = editedValue[field].map((orig, j) => idx === j ? value : orig);
      }
      else {
        // CASE C: target item is a nested element --> update it using parent index
        const innerUpdatedValue = {
          ...editedValue[field][parentIdx],
          items: editedValue[field][parentIdx].items.map((orig, j) => idx === j ? value : orig)
        }
        updatedValue = editedValue[field].map((orig, j) => parentIdx === j ? innerUpdatedValue : orig);
      }
      // update state
      setEditedValue({
        ...editedValue,
        [field]: updatedValue
      })
    }

    return (
      <DragDropContext onDragEnd={handleContentsItemDrop}>
        <Droppable droppableId={droppableId}>
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {
                elements.map((item, idx) => (
                  <Draggable
                    key={idx}
                    draggableId={`${droppableId}-${idx}`}
                    index={idx}
                  >
                    {
                      (provided) => (
                        <div ref={provided.innerRef} {...provided.draggableProps}>
                          {/* ========== CURRENT ITEM ========== */}
                          <div className="flex items-center gap-3">
                            {/* ---------- CONTROLS ---------- */}
                            <div className="flex gap-2 text-gray-400 mb-4 mr-1">
                              <div {...provided.dragHandleProps}>
                                <EllipsisVerticalIcon className="w-5 h-5"/>
                              </div>
                              <PlusIcon
                                className="w-5 h-5 cursor-pointer"
                                onClick={() => { insertAfter(idx, parentIdx); }}
                              />
                              {
                                // show new sub-list button iff nesting is allowed and current item does not already have sub-items
                                allowNesting && parentIdx === null && <ListBulletIcon
                                  className={`w-5 h-5 ${typeof editedValue[field][idx] === 'object' ? 'opacity-30' : 'cursor-pointer'}`}
                                  onClick={() => {
                                    if (typeof editedValue[field][idx] === 'object')
                                      return;
                                    makeNested(idx);
                                  }}
                                />
                              }
                              {
                                (Object.keys(elements).length > 1 || parentIdx !== null) && <XMarkIcon
                                  className="w-5 h-5 cursor-pointer"
                                  onClick={() => { deleteAt(idx, parentIdx); }}
                                />
                              }
                            </div>
                            {/* ---------- CONTENT ---------- */}
                            <div className="flex-1">
                              <Textarea
                                rows={rows}
                                value={(typeof item === 'object' ? item.title : item) ?? ''}
                                onChange={(e) => { updateValue(e.target.value, idx, parentIdx); }}
                                errorMessage={intl.formatMessage({id: 'required_field'})}
                                required={required ?? false}
                              />
                            </div>
                          </div>
                          {/* ========== SUBITEMS ========== */}
                          {
                            typeof item === 'object'
                              ? (
                                <div className="pl-28">
                                  {renderEditableList(field, required, false, idx, rows)}
                                </div>
                              )
                              : null
                          }
                        </div>
                      )
                    }
                  </Draggable>
                ))
              }
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    )
  }

  const renderList = (items, nested=false) => {
    return (
      <ul
        className={`mt-2 ${nested ? "mb-2 pl-4" : "mb-5 pl-8"}`}
        style={{listStyleType: nested ? 'circle' : 'disc'}}>
        {
          items.map((item, itemIdx) => (
            <li key={itemIdx} className="whitespace-pre-wrap pl-3 mb-1">
              {
                // ---------- RENDER CURRENT ITEM ----------
                item === null
                  ? <Placeholder width="w-32" classNames="mt-2"/>
                  : (typeof(item) === 'object' ? item.title : item)
              }
              {
                // ---------- RENDER SUBITEMS (IF ANY) ----------
                typeof(item) === 'object' && renderList(item.items, true)
              }
            </li>
          ))
        }
      </ul>
    )
  }

  const normalizeEdits = () => Object.entries(editedValue).reduce(
    (prev, [field, value]) => {
      const fieldType = structure[field].type;
      switch (fieldType) {
        case 'string':
        case 'text':
          return {
            ...prev,
            [field]: value?.trim() ? value.trim() : null
          };
        case 'natural':
          return {
            ...prev,
            [field]: value.value > 0 ? value : null
          }
        case 'items':
        case 'nested_items':
          const filteredItems = value
            .map((i) => typeof(i) === 'object'
              ? (
                // remove the nested items that don't have a title (i.e. the system)
                i.title.trim()
                  ? {
                    title: i.title.trim(),
                    items: i.items
                      .map((ni) => ni.trim())
                      .filter((ni) => !!ni)
                  }
                  : null
              )
              : i.trim()
            )
            .filter((i) => !!i);
          return {
            ...prev,
            [field]: filteredItems.length > 0 ? filteredItems : null
          };
        default:
          throw Error(`unhandled reference content type '${fieldType}'`);
      }
    },
    {}
  )

  const validateEdits = (editsObject) => !Object.entries(structure)
    .some(([field, {required}]) => required && !editsObject[field])

  return (
    <PageSection
        title={_.capitalize(intl.formatMessage({id: title}))}
        editMode={editable ? !!editedValue : null}
        onEdit={onEdit}
        onConfirm={async () => {
          setLoading(true)
          const normalized = normalizeEdits();
          if (validateEdits(normalized))
            await onSave(normalized);
          else
            push({type: 'error', title: intl.formatMessage({id: 'error_fill_required_fields'})})
          setLoading(false);
        }}
        onCancel={onCancel}
        loading={loading}
        intl={intl}
      >
        {
          !editedValue && !value
            ? <div className="w-full h-28 text-md text-gray-400 flex items-center justify-center gap-2">
              <InformationCircleIcon className="w-5 h-5"/>
              <span>{valueNotAvailableMessage}</span>
            </div>
            : <>
              {
                editedValue
                  ? Object.entries(structure).map(([field, {type, required, rows, um}], idx) => {
                    let section;
                    switch (type) {
                      case 'string':
                        section = <Input
                          value={editedValue[field] ?? ''}
                          onChange={(e) => {
                            setEditedValue({
                              ...editedValue,
                              [field]: editedValue[field] = e.target.value
                            })
                          }}
                          validateOnMount={required ?? false}
                          errorMessage={intl.formatMessage({id: 'required_field'})}
                          required={required ?? false}
                        />
                        break;
                      case 'natural':
                        section = <div className="grid grid-cols-4 gap-3">
                          <div className={um ? 'col-span-3' : 'col-span-4'}>
                            <Input
                              type="number"
                              label={um ? intl.formatMessage({id: 'value'}) : null}
                              min={0}
                              value={editedValue[field]?.value ?? 0}
                              onChange={(e) => {
                                setEditedValue({
                                  ...editedValue,
                                  [field]: {
                                    ...editedValue[field],
                                    value: e.target.value
                                  }
                                })
                              }}
                              errorMessage={intl.formatMessage({id: 'required_field'})}
                              validateOnMount={required ?? false}
                              required={required ?? false}
                            />
                          </div>
                          {
                            um && <div className="col-span-1">
                              <Input
                                label={intl.formatMessage({id: 'measurement_unit'})}
                                value={editedValue[field]?.um}
                                onChange={(e) => {
                                  setEditedValue({
                                    ...editedValue,
                                    [field]: {
                                      ...editedValue[field],
                                      um: e.target.value
                                    }
                                  })
                                }}
                                errorMessage={intl.formatMessage({id: 'required_field'})}
                                validateOnMount={required ?? false}
                                required={required ?? false}
                              />
                            </div>
                          }
                        </div>
                        break;
                      case 'text':
                        section = <Textarea
                          value={editedValue[field] ?? ''}
                          onChange={(e) => {
                            setEditedValue({
                              ...editedValue,
                              [field]: setEditedValue[field] = e.target.value
                            })
                          }}
                          errorMessage={intl.formatMessage({id: 'required_field'})}
                          required={required ?? false}
                        />
                        break;
                      case 'items':
                      case 'nested_items':
                        section = renderEditableList(field, required, type === 'nested_items', null, rows);
                        break;
                      default:
                        throw Error(`Unhandled reference content type '${type}'`)
                    }

                    const tooltipLocaleKey = `${title}_${field}_helper`;
                    return <div key={idx} className="my-2">
                      <h3 className="flex gap-2 mb-1">
                        <span className="font-bold">{intl.formatMessage({id: field})}</span>
                        {
                          /* Render tooltip iff helper text is set in locale (for this section and field) */
                          !!intl.messages[tooltipLocaleKey] && (
                            <Tooltip
                              text={intl.formatMessage({id: tooltipLocaleKey})}
                              placement="bottom-end"
                              size="small"
                            />
                          )
                        }
                      </h3>
                      {section}
                    </div>
                  })
                  : Object.entries(structure)
                    .filter(([field, {type}]) => !!(value?.[field]))  // skip if current field is null
                    .map(([field, {type}], idx) => {
                      // render field based on its type
                      switch (type) {
                        case 'string':
                        case 'natural':
                          return <h3 key={idx} className="flex items-start gap-2 mb-5">
                            <span className="font-bold">{intl.formatMessage({id: field})}:</span>
                            {
                              value
                                ? <span className="whitespace-pre-wrap">
                                  {
                                    type === 'string'
                                      ? value[field]
                                      : `${value[field].value} ${value[field].um}`
                                  }
                                </span>
                                : <Placeholder width="w-32" classNames="mt-2"/>
                            }
                          </h3>;
                        case 'text':
                          return <div key={idx}>
                            <h3>{intl.formatMessage({id: field})}</h3>
                            <div className="mt-2 mb-5 whitespace-pre-wrap">
                              {
                                value
                                  ? value[field]
                                  : Array(2).fill(null).map((_, pIdx) => (
                                    <Placeholder key={pIdx} width="w-full"/>
                                  ))
                              }
                            </div>
                          </div>
                        case 'items':
                        case 'nested_items':
                          return <div key={idx}>
                            <h3>{intl.formatMessage({id: field})}</h3>
                            {
                              renderList(value?.[field] ?? Array(3).fill(null))
                            }
                          </div>
                        default:
                          throw Error(`Unhandled reference content type '${type}'`)
                      }
                  })
              }
            </>
        }
        {/* ---------- EXTRA ---------- */}
        {children}
      </PageSection>
  )
}

export default ReferenceJsonEditorSection;